/**
 * Topology layout (OP#1857) -- PURE (no React), unit-tested by topologyLayout.test.ts and drawn
 * by routes/Topology.tsx.
 *
 * WHY: the old page placed nodes by LIST POSITION in three fixed tiers (unmanaged switch /
 * managed switch / everything else). An AP whose uplink was the second switch could sit under
 * the first one, and its straight line to its real switch ran behind the unrelated box -- so the
 * AP read as cabled through the wrong switch (image 7: "1 links" but two segments).
 *
 * NOW: a tidy tree. Each node's `parent` (its LLDP uplink, from the controller) decides where it
 * goes: every child sits in the column span directly UNDER its parent, and links are drawn as
 * elbows (down, across in the gap between rows, down) so a line never crosses a box.
 *   - Roots (no parent): unmanaged upstream switches first, then gateways, switches, the rest.
 *   - A managed device with no LLDP neighbour and nothing under it goes on a separate
 *     "Uplink unknown" shelf below the tree instead of floating as a fake root.
 *   - Cycles (A says B, B says A) are tolerated: a node is placed once, at its first visit.
 *   - An older controller that sends no `parent` still works: the parent is derived from the
 *     node's first outgoing edge.
 */

/** The node fields the layout reads (a subset of the API's TopoNode). */
export interface LayoutNode {
  id: string;
  kind: string;
  managed: boolean;
  label?: string;
  parent?: string | null;
  uplink?: "lldp" | "unknown" | "root" | null;
}

/** The edge fields the layout reads. */
export interface LayoutEdge {
  source: string;
  target: string;
}

export interface Dims {
  nodeW: number;
  nodeH: number;
  colGap: number;
  rowGap: number;
  pad: number;
  /** Vertical room above the shelf row for its caption. */
  shelfCaption: number;
}

export const DEFAULT_DIMS: Dims = { nodeW: 150, nodeH: 44, colGap: 28, rowGap: 90, pad: 24, shelfCaption: 34 };

export interface Point { x: number; y: number }

export interface TopoLayout {
  /** Top-left corner of each node's box. */
  pos: Map<string, Point>;
  /** Tree depth per node (0 = root). Shelf nodes are absent. */
  depth: Map<string, number>;
  /** Resolved parent per node (null for roots and shelf nodes). */
  parentOf: Map<string, string | null>;
  /** Ids on the "Uplink unknown" shelf, in display order. */
  shelf: string[];
  /** y of the shelf caption baseline, or null when the shelf is empty. */
  shelfCaptionY: number | null;
  width: number;
  height: number;
}

/** Root ordering: unmanaged upstream, gateway, switch, other; then label/id for stability. */
function rootRank(n: LayoutNode): number {
  if (!n.managed) return 0;
  if (n.kind === "gateway") return 1;
  if (n.kind === "switch") return 2;
  return 3;
}

function byRankThenLabel(a: LayoutNode, b: LayoutNode): number {
  const r = rootRank(a) - rootRank(b);
  if (r !== 0) return r;
  return (a.label ?? a.id).localeCompare(b.label ?? b.id);
}

/** Resolve every node's parent: the controller's `parent`, else its first edge; loop-safe. */
export function resolveParents(nodes: LayoutNode[], edges: LayoutEdge[]): Map<string, string | null> {
  const ids = new Set(nodes.map((n) => n.id));
  const parentOf = new Map<string, string | null>();
  const firstEdge = new Map<string, string>();
  for (const e of edges) if (!firstEdge.has(e.source)) firstEdge.set(e.source, e.target);

  const wouldCycle = (child: string, parent: string): boolean => {
    const seen = new Set<string>();
    let cur: string | null | undefined = parent;
    while (cur && !seen.has(cur)) {
      if (cur === child) return true;
      seen.add(cur);
      cur = parentOf.get(cur);
    }
    return false;
  };

  for (const n of nodes) {
    // `parent` present (even null) = the controller decided; absent = derive from edges.
    const cand = n.parent !== undefined ? n.parent : firstEdge.get(n.id) ?? null;
    const ok = cand && cand !== n.id && ids.has(cand) && !wouldCycle(n.id, cand);
    parentOf.set(n.id, ok ? cand : null);
  }
  return parentOf;
}

/** Lay the graph out as a tidy tree plus the "Uplink unknown" shelf. */
export function layoutTopology(nodes: LayoutNode[], edges: LayoutEdge[], dims: Dims = DEFAULT_DIMS): TopoLayout {
  const { nodeW, nodeH, colGap, rowGap, pad, shelfCaption } = dims;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const parentOf = resolveParents(nodes, edges);

  const children = new Map<string, LayoutNode[]>();
  for (const n of nodes) {
    const p = parentOf.get(n.id);
    if (p) {
      const list = children.get(p) ?? [];
      list.push(n);
      children.set(p, list);
    }
  }
  for (const list of children.values()) list.sort(byRankThenLabel);

  // Shelf: a MANAGED device that reports no uplink and carries nothing below it.
  const isShelf = (n: LayoutNode) =>
    n.managed && !parentOf.get(n.id) && !(children.get(n.id)?.length) && n.uplink === "unknown";
  const shelf = nodes.filter(isShelf).sort(byRankThenLabel).map((n) => n.id);
  const shelfSet = new Set(shelf);
  const roots = nodes.filter((n) => !parentOf.get(n.id) && !shelfSet.has(n.id)).sort(byRankThenLabel);

  // Subtree widths (memoised; `visiting` guards against any residual loop).
  const widthOf = new Map<string, number>();
  const visiting = new Set<string>();
  const subtreeW = (id: string): number => {
    const memo = widthOf.get(id);
    if (memo !== undefined) return memo;
    if (visiting.has(id)) return nodeW;
    visiting.add(id);
    const kids = children.get(id) ?? [];
    const kidsW = kids.reduce((s, k) => s + subtreeW(k.id), 0) + Math.max(0, kids.length - 1) * colGap;
    visiting.delete(id);
    const w = Math.max(nodeW, kidsW);
    widthOf.set(id, w);
    return w;
  };

  const pos = new Map<string, Point>();
  const depth = new Map<string, number>();
  let maxDepth = -1;
  const place = (id: string, left: number, d: number) => {
    if (pos.has(id)) return; // placed once, at its first visit
    const w = subtreeW(id);
    pos.set(id, { x: left + (w - nodeW) / 2, y: pad + d * (nodeH + rowGap) });
    depth.set(id, d);
    maxDepth = Math.max(maxDepth, d);
    const kids = children.get(id) ?? [];
    const kidsW = kids.reduce((s, k) => s + subtreeW(k.id), 0) + Math.max(0, kids.length - 1) * colGap;
    let x = left + (w - kidsW) / 2;
    for (const k of kids) {
      place(k.id, x, d + 1);
      x += subtreeW(k.id) + colGap;
    }
  };

  let cursor = pad;
  for (const r of roots) {
    place(r.id, cursor, 0);
    cursor += subtreeW(r.id) + colGap;
  }
  const treeW = roots.length ? cursor - colGap + pad : 0;
  let height = maxDepth >= 0 ? pad + (maxDepth + 1) * (nodeH + rowGap) - rowGap + pad : 0;

  // Shelf row below the tree, left-aligned under a caption.
  let shelfCaptionY: number | null = null;
  let shelfW = 0;
  if (shelf.length) {
    const top = (height || pad) + shelfCaption;
    shelfCaptionY = top - 16; // clear of the 8px dashed stub above each shelved box
    shelf.forEach((id, i) => pos.set(id, { x: pad + i * (nodeW + colGap), y: top }));
    shelfW = pad * 2 + shelf.length * nodeW + (shelf.length - 1) * colGap;
    height = top + nodeH + pad;
  }

  // Any node not reached (only possible with a malformed parent chain): park it on the shelf row.
  for (const n of nodes) if (!pos.has(n.id) && byId.has(n.id)) {
    pos.set(n.id, { x: pad, y: height });
    height += nodeH + colGap;
  }

  for (const id of shelf) parentOf.set(id, null);
  return { pos, depth, parentOf, shelf, shelfCaptionY, width: Math.max(treeW, shelfW, pad * 2 + nodeW), height };
}

/**
 * SVG path for a link from a child box up to its parent box: up from the child's top-centre to
 * the middle of the row gap, across, then up into the parent's bottom-centre. The horizontal run
 * sits in the empty gap between rows, so it never passes through a box.
 */
export function elbowPath(child: Point, parent: Point, dims: Dims = DEFAULT_DIMS): string {
  const cx = child.x + dims.nodeW / 2;
  const cy = child.y;
  const px = parent.x + dims.nodeW / 2;
  const py = parent.y + dims.nodeH;
  if (cy <= py) {
    // Not below its target (a loop edge or a shelf oddity): a plain straight line.
    return `M ${cx} ${cy} L ${px} ${py}`;
  }
  const midY = cy - dims.rowGap / 2;
  return `M ${cx} ${cy} V ${midY} H ${px} V ${py}`;
}
